import { STYLES_VARIABLES } from 'constants/styleVariables';
import PenIcon from 'assets/icons/pen.png';
import RedPenIcon from 'assets/icons/pen_red.png';
import BluePenIcon from 'assets/icons/pen_blue.png';
import GreenPenIcon from 'assets/icons/pen_green.png';
import EraserIcon from 'assets/icons/eraser.png';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import Stroke1 from 'assets/icons/stroke1.png';
import Stroke2 from 'assets/icons/stroke2.png';
import Stroke3 from 'assets/icons/stroke3.png';
import Stroke4 from 'assets/icons/stroke4.png';
import Stroke5 from 'assets/icons/stroke5.png';
import { useEffect, useState } from 'react';
import styles from 'styles/components/pages/chart/Pentool.module.scss';
import classNames from 'classnames';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import Cancel from 'assets/icons/cancel.png';
import Undo from 'assets/icons/undo.png';
import CancelDisabled from 'assets/icons/cancel_disabled.png';
import UndoDisabled from 'assets/icons/undo_disabled.png';
import { TLine } from 'types/chart/pentool';

type TProps = {
  greenPen?: boolean;
  setTool: (tool: TTool) => void;
  strokeActive?: boolean;
  setStrokeActive?: (active: boolean) => void;
  setStrokeWidth: (stroke: number) => void;
  setStrokeColor: (color: string) => void;
  isRegister?: boolean;
  feedbackLines?: TLine[];
  setFeedbackLines?: (lines: TLine[]) => void;
  activeTabIndex?: number;
  maxLines?: TLine[];
  setMaxLines?: (lines: TLine[]) => void;
  mandLines?: TLine[];
  setMandLines?: (lines: TLine[]) => void;
};

const Pentool = ({
  greenPen,
  setTool,
  strokeActive,
  setStrokeActive,
  setStrokeWidth,
  setStrokeColor,
  isRegister,
  feedbackLines,
  setFeedbackLines,
  activeTabIndex,
  maxLines,
  setMaxLines,
  mandLines,
  setMandLines,
}: TProps) => {
  const [isOpenPenMenu, setIsOpenPenMenu] = useState(false);
  const [isOpenStrokeMenu, setIsOpenStrokeMenu] = useState(false);
  const [selectedPen, setSelectedPen] = useState(greenPen ? 4 : 3);
  const [selectedStroke, setSelectedStroke] = useState(2);
  const [cancelFeedbackLines, setCancelFeedbackLines] = useState<TLine[]>([]);
  const [cancelMaxLines, setCancelMaxLines] = useState<TLine[]>([]);
  const [cancelMandLines, setCancelMandLines] = useState<TLine[]>([]);
  const [isFeedbackUpdated, setIsFeedbackUpdated] = useState(false);

  const commonPenIcons = [EraserIcon, BluePenIcon, RedPenIcon, PenIcon];
  const strokeImages = [Stroke1, Stroke2, Stroke3, Stroke4, Stroke5];

  const penIcons = greenPen
    ? [EraserIcon, GreenPenIcon, BluePenIcon, RedPenIcon, PenIcon]
    : commonPenIcons;

  const onClickPen = () => {
    if (!isRegister) {
      return;
    }

    setStrokeActive?.(true);
    setIsOpenPenMenu(true);
    setTool('pen');
  };

  const onClickClose = () => {
    setIsOpenPenMenu(false);
    setIsOpenStrokeMenu(false);
    setStrokeActive && setStrokeActive(false);
  };

  const onClickOutside = () => {
    setIsOpenPenMenu(false);
    setIsOpenStrokeMenu(false);
  };

  const onClickPenDetail = (index: number) => {
    setSelectedPen(index);
    setIsOpenStrokeMenu(true);

    const colors: Record<number, string> = {
      1: greenPen ? STYLES_VARIABLES.GREEN_01 : STYLES_VARIABLES.BLUE,
      2: greenPen ? STYLES_VARIABLES.BLUE : STYLES_VARIABLES.RED_01,
      3: greenPen ? STYLES_VARIABLES.RED_01 : STYLES_VARIABLES.BLACK,
      4: STYLES_VARIABLES.BLACK,
    };
    if (colors[index]) {
      setStrokeColor(colors[index]!);
    }
    setTool(index === 0 ? 'eraser' : 'pen');
  };

  const onClickStroke = (index: number) => {
    setSelectedStroke(index);
    setIsOpenStrokeMenu(false);
    setStrokeWidth(index + 1);
  };

  const onClickCancel = () => {
    if (!feedbackLines) return;
    const updatedFeedbackLines = feedbackLines.slice(0, -1);
    const deletedLine = feedbackLines.slice(-1);
    setFeedbackLines?.(updatedFeedbackLines);
    setCancelFeedbackLines((prev) => [...prev, ...deletedLine]);

    const isLineInArray = (lines: TLine[] | undefined) =>
      lines?.some(
        (line) =>
          line.points.length === deletedLine[0].points.length &&
          line.points.every(
            (point, idx) => point === deletedLine[0].points[idx]
          ) &&
          line.strokeColor === deletedLine[0].strokeColor &&
          line.strokeWidth === deletedLine[0].strokeWidth &&
          line.tool === deletedLine[0].tool
      );

    if (isLineInArray(maxLines)) {
      setCancelMaxLines((prev) => [...prev, ...deletedLine]);
      setMaxLines?.(maxLines!.filter((line) => !deletedLine.includes(line)));
    }

    if (isLineInArray(mandLines)) {
      setCancelMandLines((prev) => [...prev, ...deletedLine]);
      setMandLines?.(mandLines!.filter((line) => !deletedLine.includes(line)));
    }
  };

  const onClickUndo = () => {
    if (!cancelFeedbackLines.length) return;
    const restoredLine = cancelFeedbackLines.slice(-1);
    setFeedbackLines?.([...feedbackLines!, ...restoredLine]);
    setCancelFeedbackLines(cancelFeedbackLines.slice(0, -1));

    const isLineInArray = (lines: TLine[] | undefined) =>
      lines?.some(
        (line) =>
          line.points.length === restoredLine[0].points.length &&
          line.points.every(
            (point, idx) => point === restoredLine[0].points[idx]
          ) &&
          line.strokeColor === restoredLine[0].strokeColor &&
          line.strokeWidth === restoredLine[0].strokeWidth &&
          line.tool === restoredLine[0].tool
      );

    if (isLineInArray(cancelMaxLines)) {
      setMaxLines?.([...maxLines!, ...restoredLine]);
      setCancelMaxLines(cancelMaxLines.slice(0, -1));
    }

    if (isLineInArray(cancelMandLines)) {
      setMandLines?.([...mandLines!, ...restoredLine]);
      setCancelMandLines(cancelMandLines.slice(0, -1));
    }
  };

  useEffect(() => {
    if (feedbackLines) {
      setFeedbackLines?.(feedbackLines);
      setIsFeedbackUpdated(true);
    }
  }, [feedbackLines]);

  useEffect(() => {
    if (isFeedbackUpdated) {
      setCancelFeedbackLines([]);
      setSelectedPen(3);
    }
  }, [activeTabIndex]);

  return (
    <>
      {isOpenPenMenu && (
        <div
          className={styles.dimmed}
          onClick={onClickOutside}
          onTouchStart={onClickOutside}
        ></div>
      )}

      <div className={classNames(styles.pen_wrapper, 'excluded2')}>
        <div
          style={{
            width: '54px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px solid ${STYLES_VARIABLES.PURPLE_03}`,
            borderRadius: '6px',
            backgroundColor: STYLES_VARIABLES.PURPLE_04,
          }}
        >
          {feedbackLines && feedbackLines.length > 0 ? (
            <img
              src={Cancel}
              width={20}
              alt="back_arrow"
              style={{ padding: '16px' }}
              onClick={onClickCancel}
            />
          ) : (
            <img
              src={CancelDisabled}
              width={20}
              alt="cancel_disabled_arrow"
              style={{ padding: '16px' }}
            />
          )}
          <div
            style={{
              width: '32px',
              borderBottom: `1px solid ${STYLES_VARIABLES.PURPLE_03}`,
            }}
          ></div>

          {cancelFeedbackLines.length === 0 ? (
            <img
              src={UndoDisabled}
              width={20}
              alt="undo_disabled_arrow"
              style={{ padding: '16px' }}
            />
          ) : (
            <img
              src={Undo}
              width={20}
              alt="back_arrow"
              style={{ padding: '16px' }}
              onClick={onClickUndo}
            />
          )}
        </div>

        <div className={styles.pen_container}>
          <div>
            <div
              className={
                isRegister
                  ? styles.pen_controller
                  : styles.pen_controller_disabled
              }
            >
              {isOpenPenMenu && (
                <>
                  {penIcons.map((src, index) => (
                    <img
                      key={index}
                      src={src}
                      alt={`pen${index + 1}`}
                      className={
                        index === selectedPen ? styles.pen_active : styles.pen
                      }
                      onClick={() => onClickPenDetail(index)}
                    />
                  ))}
                </>
              )}
              {!isOpenPenMenu && (
                <img
                  src={penIcons[selectedPen]}
                  alt="selected pen"
                  className={strokeActive ? styles.pen_active : styles.pen}
                  onClick={onClickPen}
                />
              )}
            </div>
            {isOpenPenMenu && (
              <div className={styles.close_container}>
                <CloseIcon
                  width={24}
                  height={24}
                  fill={STYLES_VARIABLES.GRAY_08}
                  onClick={onClickClose}
                />
              </div>
            )}
          </div>

          {isOpenStrokeMenu && (
            <div
              className={classNames(styles.stroke_container, {
                [styles.stroke_container_lg]: greenPen,
              })}
            >
              {strokeImages.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`stroke${index + 1}`}
                  className={
                    index === selectedStroke
                      ? styles.stroke_active
                      : styles.stroke
                  }
                  onClick={() => onClickStroke(index)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Pentool;
